/**
 * Package-parity record for the shared UI vocabulary.
 *
 * design-dashboard and the production dashboard should render the same atoms and
 * molecules out of `@ayble-health/dashboard-components` (ENG-63). This file
 * records, per component, where each one now comes from — so the audit page
 * answers "is this ours, theirs, or theirs-with-a-patch?" instead of leaving a
 * reader to guess.
 *
 * ## How these claims were measured
 *
 * Verified against the **package itself** at the version below — cloned, then
 * compared file by file with the local copies as they stood on `main`
 * (`44713ab`). Both sides were prettier-normalized and had comments stripped
 * before diffing, because the package deliberately condenses doc comments: a
 * raw diff reports all 28 shared files as different when only 13 really are.
 *
 * An earlier pass measured against `Ayble-Health/dashboard @ main` as a stand-in
 * for the package, on the assumption that the package is published from there.
 * That proxy was wrong for 7 files — dashboard's `main` is stale relative to the
 * package, and in particular the package had *already* adopted this repo's
 * Button consolidation and `shadows.card` token. Do not re-measure against
 * dashboard; measure against the package.
 *
 * Dates are pinned literals, never derived — same standard as `ChangeBadge`, and
 * for the same reason: a badge is a historical claim, so it must not silently
 * re-date itself when the next comparison happens.
 */
export const PARITY_COMPARED_ON = 'Sep 2, 2026';

/** The exact thing the claims below were diffed against. */
export const PARITY_COMPARED_AGAINST =
  '@ayble-health/dashboard-components v1.3.0';

export type ParityStatus =
  /**
   * Now imported from the package. Either the code was already equivalent, or
   * the package was ahead and we took its version.
   */
  | 'shared'
  /**
   * Imported from the package, but wrapped locally to carry a documented fix
   * the package doesn't have yet. The recipe is still the package's.
   */
  | 'patched'
  /** No equivalent in the package. Stays in this repo either way. */
  | 'local'
  /**
   * The package exports it and we don't consume it yet. Not a gap to close
   * automatically — adopting it is a separate design decision.
   */
  | 'package-only';

type ParityEntry = {
  status: ParityStatus;
  /** Where the component lives now, from this repo's point of view. */
  source: string;
  /**
   * Required for everything except plain `shared`: what differed, which
   * direction it pointed, and what was decided.
   */
  note?: string;
};

/**
 * Keyed by the name each component is displayed under in the audit, so a
 * `<ParityBadge name="..." />` typo is a compile error rather than a silently
 * missing badge. Components without their own specimen are still recorded — the
 * record is meant to be complete, not just what happens to render.
 */
const ENTRIES = {
  // ---- Equivalent already; adopting the package changed nothing on screen --
  'Text & Heading': { status: 'shared', source: 'package' },
  'VStack / HStack': { status: 'shared', source: 'package' },
  Link: { status: 'shared', source: 'package' },
  Input: { status: 'shared', source: 'package' },
  Select: { status: 'shared', source: 'package' },
  Checkbox: { status: 'shared', source: 'package' },
  Switch: { status: 'shared', source: 'package' },
  FormControl: { status: 'shared', source: 'package' },
  Card: { status: 'shared', source: 'package' },
  Modal: { status: 'shared', source: 'package' },
  Tooltip: {
    status: 'shared',
    source: 'package',
    note: 'Code is equivalent to what we had, but note the asChild constraint: Tooltip.Trigger clones its child, and cloning a chakra() recipe component (e.g. the careOS Tag) throws inside Chakra. Wrap a plain element instead — see the span in ParityBadge. Upstream hardening candidate.',
  },

  // ---- The package was ahead; we took its version -------------------------
  FilterSelector: {
    status: 'shared',
    source: 'package',
    note: 'Package adds zIndex 10 to the dropdown — a real fix, ours could render behind sibling content.',
  },
  ErrorMessage: {
    status: 'shared',
    source: 'package',
    note: 'Package adds a real Props type; ours took an implicit any.',
  },
  SuccessMessage: {
    status: 'shared',
    source: 'package',
    note: 'Package adds a real Props type; ours took an implicit any.',
  },
  DatePicker: {
    status: 'shared',
    source: 'package',
    note: 'Package self-contains formatDateToDayOnly and its types instead of importing from AppointmentsPage, and adds an optional-chain guard on day availability. Adopting it also decouples the component from this repo internals.',
  },
  CardAccordion: {
    status: 'shared',
    source: 'package',
    note: 'Package uses the ChevronRight icon instead of a /chevron-right.png asset, and the aqua token instead of a hardcoded #5299A9.',
  },
  CardButton: {
    status: 'shared',
    source: 'package',
    note: 'Package fixes a live bug: ours mapped variant="secondary" onto a Button variant the Aug 13 audit had deleted, so Secondary rendered identically to Primary. The package maps it to solid and applies the seafoam fill explicitly.',
  },
  Accordion: {
    status: 'shared',
    source: 'package',
    note: 'Package fixes a second live bug: ours read ctx.open, but zag exposes ItemState.expanded — so isExpanded was always undefined and item content never rendered. Our [key: string]: any escape hatch is what let it typecheck. Package also tightens that to unknown.',
  },
  PanelShell: {
    status: 'shared',
    source: 'package',
    note: 'Package adds h and bg props. The soft shadows.card treatment we introduced is already upstream — no regression in taking it.',
  },
  InfoCard: {
    status: 'shared',
    source: 'package',
    note: 'Package uses its own Badge rather than Chakra’s and adds primaryButtonDisabled. shadows.card is already upstream.',
  },
  Pressable: {
    status: 'shared',
    source: 'package',
    note: 'Package adds href/target/rel with automatic rel="noreferrer" on _blank. Its href branch drops ref forwarding and pins type="button"; neither is used here today.',
  },
  Tabs: {
    status: 'shared',
    source: 'package',
    note: 'Package adds a TabBar export, count badges, fitContent, and optional content. TabBar is not consumed yet — ProviderHome/CountTabs.tsx still implements the same pattern locally and is a candidate for retirement.',
  },
  Severity: {
    status: 'shared',
    source: 'package',
    note: 'Package imports legacyPalettes from its own theme instead of the legacy theme/theme, and types the level map. Deprecated on both sides — see the Archive tab. Shared here is a fact, not a recommendation to keep it.',
  },

  // ---- Package component, local patch -------------------------------------
  Button: {
    status: 'shared',
    source: 'package',
    note: 'v1.2.2 fixed the link-variant hover background that had been patched locally — Chakra’s recipe no longer bleeds a near-black fill on hover. Re-exported from components/ui/button.tsx for call-site convenience.',
  },

  // ---- careOS-only; no package equivalent ---------------------------------
  Tag: {
    status: 'local',
    source: 'deleted — see Archive',
    note: 'Superseded by Chip. Every Tag variant now exists as a Chip: feather/seafoam/fuschia map to the package neutral/positive/negative, and aqua/canvas/symptom/mint/ballet landed upstream in v1.3.0. Gone from feature code — the last two call sites, the roster’s Cancelled and New enrollment badges, moved to Chip. PillToggleGroup and PressablePill also landed upstream in v1.3.0. ui/tag.tsx is deleted; the Archive tab keeps the eight variants as inert swatches with their values transcribed onto brand tokens, so the record survives without the component. Kept in this table because a reader who finds the name in an old branch or Figma frame still needs to land somewhere.',
  },
  PillToggleGroup: {
    status: 'shared',
    source: 'package',
    note: 'v1.3.0 adopted the local roster filter control — selection logic and PressablePill chrome now ship upstream. Re-exported from components/ui/pill-toggle-group.tsx for call-site convenience.',
  },
  PressablePill: {
    status: 'shared',
    source: 'package',
    note: 'v1.3.0 adopted the interactive counterpart to Chip. Shares pill geometry with Chip via pillBase in the package rather than matching by measurement. Re-exported from components/ui/pressable-pill.tsx for call-site convenience.',
  },
  BlockCard: {
    status: 'local',
    source:
      'designed only — production is src/components/UserDashboard/overview/LaneBlockCard.tsx',
    note: 'The Care Guide lane card. Six status states in design, one shipped. Documented under Patterns with every state rendered from live tokens, because the gap is invisible from either side: the Figma shows states nobody has built, the app shows a card nobody would recognise from the Figma. Five of the six states are Bucket 2 on the same skeleton (header tone + action label); Recommended is Bucket 3 — two actions and a rationale footer is its own anatomy. Result chips need no new component; every value is an existing Chip variant.',
  },
  Pagination: {
    status: 'local',
    source: 'src/components/ui/pagination.tsx',
    note: 'Roster-specific. No package equivalent. Composes Button so page items match every other button.',
  },
  StatusPill: {
    status: 'local',
    source: 'src/components/ProviderHome/StatusPill.tsx',
    note: 'Visit-status vocabulary. Its chrome is now Chip — it drew seven pills by hand that duplicated Chip’s vocabulary under other names, which is why the design system documented two pill sets for months. What stays here is the status→variant mapping plus the three statuses a variant cannot express: occurred renders no pill at all, unconfirmed is a control that confirms the appointment, and rescheduled composes a formatted slot time into its label. Distinct from StatusIndicatorPill, which is the careOS app-status pill.',
  },
  StatusIndicatorPill: {
    status: 'local',
    source:
      'src/components/ui/StatusIndicatorPill.tsx + StatusIndicatorIcons.tsx',
    note: 'careOS app-status vocabulary. No package equivalent. Audited under Patterns rather than as an atom.',
  },

  // ---- Exported by the package, adopted or not ----------------------------
  Badge: {
    status: 'shared',
    source: 'package',
    note: 'v1.2.3 added outline and neutral — the two square careOS Tag variants re-homed from the archive. Re-exported from components/ui/badge.tsx for call-site convenience.',
  },
  Chip: {
    status: 'patched',
    source: 'package + src/components/ui/chip.tsx',
    note: 'v1.3.0 added the six careOS variants (aqua, canvas, symptom, alert, mint, ballet) and label typography forwarding upstream. The only local patch left is vocabulary: careOS omits package `outline` in favour of `symptom` — see Archive. Re-exported from components/ui/chip.tsx with that narrowing.',
  },
  Autocomplete: {
    status: 'package-only',
    source: 'package (not consumed)',
    note: 'Debounced combobox added in v1.3.0. Not adopted here yet — ContentPreview still uses a local useDebounce hook with a plain input.',
  },
  TabBar: {
    status: 'package-only',
    source: 'package (not consumed)',
    note: 'Exported by the package but unused here. ProviderHome/CountTabs.tsx implements the same tab-bar-with-count-badges pattern locally. Replacing it is a real change to the Home screen, so it is called out rather than done silently.',
  },
} as const;

/**
 * Union of every recorded name. `ParityBadge` takes this rather than `string`,
 * which is what makes a mistyped name fail to compile.
 */
export type ParityName = keyof typeof ENTRIES;

export const COMPONENT_PARITY: Record<ParityName, ParityEntry> = ENTRIES;

export const PARITY_STATUSES: ParityStatus[] = [
  'shared',
  'patched',
  'local',
  'package-only',
];

/** Counts per status, derived so the legend can never drift from the data. */
export const parityCounts = (): Record<ParityStatus, number> => {
  const counts: Record<ParityStatus, number> = {
    shared: 0,
    patched: 0,
    local: 0,
    'package-only': 0,
  };
  (Object.keys(COMPONENT_PARITY) as ParityName[]).forEach((name) => {
    counts[COMPONENT_PARITY[name].status] += 1;
  });
  return counts;
};
